import { Component, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';

import { PasswordDeadlineComponent } from './password-deadline.component';
import { PasswordFormComponent } from './password-form.component';

/**
 * Blocking "set a new password" overlay (ZEY-3140), used while Zeymo moves from Azure AD B2C to Entra External ID: the
 * new password is set on both, so the switch later costs the user nothing. Whoever renders it decides when it is open
 * (the ZEY-3181 announcements host); it reports `completed` once the password
 * is saved. It cannot be dismissed — no close button, backdrop clicks and ESC do nothing — and the current session stays
 * valid, so nobody signs in again. The form itself is `app-password-form`, shared with the /change-password page.
 */
@Component({
  selector: 'app-password-prompt',
  standalone: true,
  imports: [TranslateModule, PasswordFormComponent, PasswordDeadlineComponent],
  templateUrl: './password-prompt.component.html',
  styleUrls: ['./password-prompt.component.scss']
})
export class PasswordPromptComponent {
  @Input() open = false;
  /**
   * ZEY-3181: when the announcements host shows it, "Not now" is offered while snoozes remain (null = unlimited) and the
   * prompt is not blocking. The host does the snooze; the prompt only asks for it.
   */
  @Input() snoozesLeft: number | null = 0;
  @Input() blocking = true;
  /** Set by the host when a snooze was refused or failed. */
  @Input() snoozeError = false;
  /** The date the password must be set by (`YYYY-MM-DD`, from the announcement's payload); null = no countdown. */
  @Input() deadline: string | null = null;
  @Output() readonly completed = new EventEmitter<void>();
  @Output() readonly snoozed = new EventEmitter<void>();
  /** Password change isn't available on our side: the server already put the prompt off; the user carries on. */
  @Output() readonly deferred = new EventEmitter<void>();

  @ViewChild(PasswordFormComponent) form?: PasswordFormComponent;

  get canSnooze(): boolean {
    return !this.blocking && (this.snoozesLeft === null || this.snoozesLeft > 0);
  }

  notNow(): void {
    if (!this.canSnooze || this.form?.busy) {
      return;
    }
    this.snoozed.emit();
  }
}
